<template>
  <div class="bd">
    <header class="bd__header">
      <p class="bd__eyebrow">{{ brand.toUpperCase() }}</p>
      <h1 class="bd__title">{{ kit?.title ?? brand }}</h1>
      <p v-if="kit?.tagline" class="bd__tagline">{{ kit.tagline }}</p>
    </header>

    <!-- Ours rather than the kit's, so this sits outside the brand kit checks below. -->
    <section v-if="sites.length" class="bd__section">
      <h2 class="bd__section-title">Websites</h2>
      <p class="bd__note">
        One theme can style several titles — the red AD regionals all render as
        <code>adr</code> — so every title on this theme is listed with its own main website and its subscription
        website, which is our own shops app.
      </p>

      <ul class="bd__sites">
        <li v-for="site in sites" :key="site.main" class="bd__site">
          <span class="bd__site-name">
            {{ site.title }}
            <span class="bd__site-codes">{{ site.codes.join(', ') }}</span>
          </span>
          <span class="bd__site-links">
            <a class="bd__link" :href="site.main" target="_blank" rel="noopener">{{ hostOf(site.main) }} ↗</a>
            <a
              v-if="site.subscription && site.subscription !== site.main"
              class="bd__link"
              :href="site.subscription"
              target="_blank"
              rel="noopener"
            >
              {{ hostOf(site.subscription) }} ↗
            </a>
            <span v-else class="bd__site-note">
              {{ site.subscription ? 'main site is the shop' : 'no subscription website' }}
            </span>
          </span>
        </li>
      </ul>
    </section>

    <section class="bd__section">
      <h2 class="bd__section-title">Marks we ship</h2>
      <div class="bd__grid">
        <figure class="bd__card bd__card--logo">
          <figcaption class="bd__card-label">Logo</figcaption>
          <div class="bd__canvas">
            <img v-if="logoUrl" :src="logoUrl" :alt="`${kit?.title ?? brand} logo`" class="bd__logo" />
            <span v-else class="bd__canvas-empty">not in the repo</span>
          </div>
        </figure>

        <figure class="bd__card">
          <figcaption class="bd__card-label">Favicon</figcaption>
          <div class="bd__canvas">
            <img v-if="faviconUrl" :src="faviconUrl" :alt="`${kit?.title ?? brand} favicon`" class="bd__favicon" />
            <span v-else class="bd__canvas-empty">not in the repo</span>
          </div>
        </figure>
      </div>
    </section>

    <p v-if="!kit" class="bd__empty">
      No brand kit. The Brand Kit Directory has no card for <code>{{ brand }}</code> — it is either a shared theme
      rather than one title, or a brand the directory has never covered. Colours and typography still render from the
      generated tokens.
    </p>

    <p v-else-if="!kit.brandkitUrl" class="bd__empty">
      The directory lists <strong>{{ kit.title }}</strong> but links no brand kit, so there is nothing to read: the
      card is there, the file behind it is not.
    </p>

    <template v-else>
      <div class="bd__links">
        <a class="bd__link" :href="kit.brandkitUrl" target="_blank" rel="noopener">Open brand kit in Figma ↗</a>
        <a v-if="kit.brandboxUrl" class="bd__link" :href="kit.brandboxUrl" target="_blank" rel="noopener">
          Brandbox ↗
        </a>
      </div>

      <section class="bd__section">
        <h2 class="bd__section-title">What the kit documents</h2>
        <dl class="bd__grid">
          <div class="bd__card bd__stat">
            <dt>Colours</dt>
            <dd>
              <NuxtLink :to="{ path: '/figma-playground/colours', query: { brand } }">
                {{ kit.colours?.length ?? 0 }}
              </NuxtLink>
            </dd>
          </div>
          <div class="bd__card bd__stat">
            <dt>Typefaces</dt>
            <dd>
              <NuxtLink :to="{ path: '/figma-playground/typography', query: { brand } }">
                {{ kit.typography?.typefaces.length ?? 0 }}
              </NuxtLink>
            </dd>
          </div>
          <div class="bd__card bd__stat">
            <dt>Text styles</dt>
            <dd>{{ (kit.typography?.webStyles.length ?? 0) + (kit.typography?.printStyles.length ?? 0) }}</dd>
          </div>
          <div class="bd__card bd__stat">
            <dt>Asset folders</dt>
            <dd>{{ kit.assets?.length ?? 0 }}</dd>
          </div>
        </dl>
      </section>

      <section class="bd__section">
        <h2 class="bd__section-title">Assets</h2>
        <p class="bd__note">
          These are the brand kit's own Google Drive links, so access stays governed by Drive and nothing is copied
          anywhere. A link points at a section's folder rather than one file — several download buttons in a section
          commonly share a folder.
        </p>

        <ul v-if="assetSections.length" class="bd__assets">
          <li v-for="section in assetSections" :key="section.section" class="bd__asset">
            <span class="bd__asset-name">{{ section.section }}</span>
            <span class="bd__asset-links">
              <a
                v-for="(url, index) in section.urls"
                :key="url"
                class="bd__link"
                :href="url"
                target="_blank"
                rel="noopener"
              >
                {{ section.urls.length > 1 ? `Folder ${index + 1} ↗` : 'Open in Drive ↗' }}
              </a>
            </span>
          </li>
        </ul>
        <p v-else class="bd__note">This kit's download buttons carry no Drive links.</p>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { DEFAULT_BRAND, isBrand } from '../../figma-playground/brands';
  import { faviconUrlFor, logoUrlFor } from '../../figma-playground/brand-assets';
  import { groupAssets, loadBrandKit } from '../../figma-playground/brand-kits';
  import { hostOf, sitesFor } from '../../figma-playground/brand-sites';

  const route = useRoute();
  const brand = computed(() => (isBrand(route.query.brand) ? route.query.brand : DEFAULT_BRAND));

  // Plain refs with a watcher rather than useAsyncData: the snapshots are local JSON, and
  // a key interpolated at setup time would let a revisit serve another brand's payload.
  const kit = ref(await loadBrandKit(brand.value));

  watch(brand, async (name) => {
    const next = await loadBrandKit(name);
    if (brand.value === name) kit.value = next;
  });

  const assetSections = computed(() => groupAssets(kit.value?.assets));

  // Ours, not the brand kit's: the sites this theme ships on and the marks we ship,
  // shown next to what the kit says.
  const sites = computed(() => sitesFor(brand.value));
  const logoUrl = computed(() => logoUrlFor(brand.value));
  const faviconUrl = computed(() => faviconUrlFor(brand.value));

  useHead({ title: () => `${kit.value?.title ?? brand.value} · Figma DS` });
</script>

<style lang="postcss" scoped>
  .bd__header {
    margin-bottom: 1.5rem;
  }

  /* One grid for the marks and the kit's counts, on auto-fill rather than auto-fit so an
     empty track keeps its width: two cards above four then line up on the same columns. */
  .bd__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(8.5rem, 1fr));
    gap: 0.75rem;
    margin: 0;
  }

  .bd__card {
    padding: 0.875rem 1rem;
    border: 1px solid #e4e4e4;
    border-radius: 0.5rem;
  }

  /* A wordmark needs the room; a roundel does not. */
  .bd__card--logo {
    grid-column: span 2;
  }

  .bd__card-label {
    margin-bottom: 0.5rem;

    font-size: 0.6875rem;
    color: #929292;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .bd__canvas {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 3rem;
  }

  .bd__logo {
    /* The SVGs range from a wide wordmark to a square roundel, so both axes are capped
       and neither is forced — fixing both would squash half of them. */
    max-width: 100%;
    max-height: 100%;
  }

  .bd__favicon {
    width: 2.5rem;
    height: 2.5rem;
    border: 1px solid #e4e4e4;
    border-radius: 0.375rem;
  }

  .bd__canvas-empty {
    font-size: 0.75rem;
    color: #b8b8b8;
  }

  .bd__eyebrow {
    margin: 0 0 0.25rem;

    font-size: 0.6875rem;
    font-weight: 700;
    color: #929292;
    letter-spacing: 0.08em;
  }

  .bd__title {
    margin: 0 0 0.25rem;
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .bd__tagline {
    margin: 0;
    font-size: 1rem;
    color: #545454;
  }

  .bd__empty {
    max-width: 44rem;
    margin: 0;
    padding: 1rem 1.25rem;
    border: 1px solid #e4e4e4;
    border-radius: 0.5rem;

    font-size: 0.875rem;
    line-height: 1.6;
    color: #545454;

    background: #fafafa;
  }

  .bd__links {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .bd__link {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #545454;
    text-decoration: none;

    &:hover {
      color: #1a1a1a;
      text-decoration: underline;
    }
  }

  .bd__section {
    margin-bottom: 2.5rem;
  }

  .bd__section-title {
    margin: 0 0 0.75rem;

    font-size: 0.6875rem;
    font-weight: 700;
    color: #929292;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .bd__note {
    max-width: 44rem;
    margin: 0 0 1rem;

    font-size: 0.8125rem;
    line-height: 1.6;
    color: #727272;
  }

  .bd__stat {
    & dt {
      margin-bottom: 0.25rem;

      font-size: 0.6875rem;
      color: #929292;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    & dd {
      margin: 0;
      font-size: 1.375rem;
      font-weight: 700;

      & a {
        color: inherit;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .bd__assets,
  .bd__sites {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    margin: 0;
    padding: 0;

    list-style: none;
  }

  .bd__site {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    align-items: baseline;
    justify-content: space-between;

    padding: 0.625rem 0.875rem;
    border: 1px solid #f2f2f2;
    border-radius: 0.375rem;
  }

  .bd__site-name {
    font-size: 0.875rem;
    font-weight: 600;
  }

  .bd__site-codes {
    margin-left: 0.5rem;

    font-size: 0.6875rem;
    font-weight: 400;
    color: #929292;
    letter-spacing: 0.04em;
  }

  .bd__site-links {
    display: flex;
    gap: 0.875rem;
  }

  .bd__site-note {
    font-size: 0.8125rem;
    color: #929292;
  }

  .bd__asset {
    display: flex;
    gap: 1rem;
    align-items: baseline;
    justify-content: space-between;

    padding: 0.625rem 0.875rem;
    border: 1px solid #f2f2f2;
    border-radius: 0.375rem;
  }

  .bd__asset-name {
    font-size: 0.875rem;
    font-weight: 600;
  }

  .bd__asset-links {
    display: flex;
    gap: 0.875rem;
  }
</style>
